import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
  {
    q: "What is a mortgage broker?",
    a: "A mortgage broker is a professional who helps you find the most suitable mortgage deals by comparing options from a wide range of lenders. We act as an intermediary between you and the lenders, ensuring you get the best mortgage for your financial situation and goals.",
  },
  {
    q: "How is a mortgage broker different from a bank or building society?",
    a: "Unlike banks or building societies, which can only offer their own products, we have access to a wide selection of mortgages from multiple lenders, including high street banks, specialist lenders, and building societies. This gives you more choice and the potential for better rates and terms.",
  },
  {
    q: "What types of mortgages do you offer?",
    a: "We offer a variety of mortgage products, including: Fixed-rate mortgages, Variable-rate mortgages, Tracker mortgages, Buy-to-let mortgages, Help to Buy and shared ownership schemes, Offset mortgages, and Remortgaging options.",
  },
  {
    q: "How much can I borrow for a mortgage?",
    a: "The amount you can borrow depends on factors such as your income, outgoings, credit history, and the size of your deposit. Most lenders offer between 4 and 5 times your annual income, though this varies depending on the lender. We can assess your affordability and help you find the right loan amount for your circumstances.",
  },
  {
    q: "What is the minimum deposit required for a mortgage?",
    a: "The minimum deposit typically required is 5% of the property's value, but many lenders prefer a deposit of 10% or more for better rates. For first-time buyers or those using government schemes, there may be lower deposit options available.",
  },
  {
    q: "How do I know if I'm eligible for a mortgage?",
    a: "Eligibility for a mortgage depends on several factors, including your income, credit score, deposit size, and current debts. We'll help you assess your financial situation and guide you through the application process to ensure you're eligible for the best mortgage options available.",
  },
  {
    q: "What is the difference between a fixed-rate and a variable-rate mortgage?",
    a: "Fixed-rate mortgages have an interest rate that stays the same for a set period, typically 2, 5, or 10 years, providing predictability in monthly payments. Variable-rate mortgages have an interest rate that can change, usually based on the Bank of England base rate. This means your payments could increase or decrease over time.",
  },
  {
    q: "What are closing costs or fees when taking out a mortgage?",
    a: "In addition to the deposit, you'll need to budget for fees such as: Arrangement fees charged by lenders, Valuation fees to assess the property value, Legal fees for conveyancing services, and Stamp Duty — a tax on property purchases above a certain value. We'll help you understand and budget for all costs involved.",
  },
  {
    q: "What is Stamp Duty, and do I have to pay it?",
    a: "Stamp Duty is a tax levied on property purchases above a certain price threshold. The amount you pay depends on the property value and whether you're a first-time buyer. There are various exemptions and discounts, including relief for first-time buyers on properties up to £500,000. We can help you understand your Stamp Duty liability.",
  },
  {
    q: "How long does the mortgage application process take?",
    a: "The mortgage application process typically takes between 4 and 6 weeks, depending on the lender and the complexity of your situation. We will work with you closely to make the process as smooth as possible and keep you updated at every stage.",
  },
  {
    q: "Can I get a mortgage if I have bad credit?",
    a: "It is possible to get a mortgage with bad credit, but you may need to consider specialist lenders who offer mortgages to those with a less-than-perfect credit history. Interest rates may be higher, but we can help you find the best available deal for your situation.",
  },
  {
    q: "What is mortgage life insurance?",
    a: "Mortgage life insurance is a type of life insurance designed to pay off your mortgage in the event of your death. It ensures that your family won't be burdened with the mortgage if something happens to you. We can help you explore life insurance options to protect your home and loved ones.",
  },
  {
    q: "Can I remortgage my property?",
    a: "Yes! Remortgaging involves switching your existing mortgage to a new deal, either with your current lender or a new one. People typically remortgage to get a better interest rate, release equity from their property, or change the terms of their loan. We can assist you in finding the best remortgage deals.",
  },
  {
    q: 'What is a "credit score," and how does it affect my mortgage application?',
    a: "Your credit score is a number that reflects your creditworthiness based on your credit history. Lenders use it to assess how risky it is to lend to you. A higher score generally leads to better mortgage offers, while a lower score may result in higher interest rates or limited borrowing options. We can guide you through improving your credit score if needed.",
  },
  {
    q: "How do I get started with a mortgage application?",
    a: "To get started, simply contact us for an initial consultation. We will review your financial situation, explain the mortgage options available, and help you begin the application process. Our goal is to make the process simple and stress-free, with expert advice every step of the way.",
  },
];

export default function FaqsPage() {
  return (
    <div data-testid="faqs-page">
      {/* Hero Banner */}
      <section className="relative bg-[#111111] py-32 lg:py-40">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1920&q=80')] bg-cover bg-center opacity-15" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#d64241] font-semibold text-sm tracking-widest uppercase mb-3 font-body">
            FAQs
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight">
            Frequently Asked<br />
            <span className="text-[#d64241]">Questions</span>
          </h1>
        </div>
      </section>

      {/* FAQ Section */}
      <section data-testid="faq-list" className="py-20 lg:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-black tracking-tight mb-4">
              Here Are the Answers to Most Frequently Asked Questions
            </h2>
            <p className="text-base md:text-lg text-gray-600 font-body">
              Can't find what you're looking for?{" "}
              <Link to="/contact" className="text-[#d64241] font-medium hover:underline">
                Contact us
              </Link>
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                data-testid={`faq-item-${i}`}
                className="bg-gray-50 rounded-xl border border-gray-100 px-6 data-[state=open]:bg-white data-[state=open]:shadow-lg data-[state=open]:border-[#fde8e8] transition-all duration-200"
              >
                <AccordionTrigger
                  data-testid={`faq-trigger-${i}`}
                  className="text-left text-black font-medium hover:no-underline py-5 text-base"
                >
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed font-body pb-5">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black tracking-tight mb-6">
            Still Have Questions?
          </h2>
          <p className="text-lg text-gray-600 mb-10 font-body">
            Our team is here to help. Get in touch with us and we'll be happy to answer any
            questions you may have.
          </p>
          <Link to="/contact">
            <Button
              data-testid="faqs-cta-btn"
              className="bg-[#d64241] hover:bg-[#b53736] text-white rounded-full px-8 py-6 text-base font-semibold shadow-lg shadow-[#d64241]/25 hover:-translate-y-0.5 transition-all duration-200"
            >
              Contact Us
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
